/**
 * The "On the desk" line on an admin Quality Routing row (proposal 12,
 * kanban 0aac3112): where the thread sits on the quality door, who it waits
 * on, the door URL that opens it, and one pill per ask sent on it.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DeskLocation, ThreadAsk, TrackedThread } from '../services/qualityRouting';

export const DOOR = '/portal/quality-copilot';

export const DESK_LABELS: Record<DeskLocation, string> = {
  triage: 'Triage',
  cases: 'Cases',
  done: 'Done',
  routed_away: 'Routed away',
};

/** "Jess Finlaw" -> "Jess"; the row is a scent, the With column has the full name. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** Whose move a case is waiting on, from its court; null when unknown. */
function courtWaitsOn(court: string | null | undefined, routedTo: string | null): string | null {
  switch (court) {
    case 'waiting_on_customer':
      return 'waiting on the customer';
    case 'waiting_on_colleague':
      return 'waiting on a colleague';
    case 'ready_to_close':
      return 'ready to close';
    default:
      // outstanding_on_us (or no court known): the desk holding it.
      return routedTo && routedTo.trim() ? `waiting on ${firstName(routedTo)}` : null;
  }
}

type DeskFields = Pick<TrackedThread, 'routed_to' | 'case_court'> & { desk_location: DeskLocation };

/** "Triage · waiting on Jess" / "Cases · waiting on the customer" / "Done". */
export function deskLabel(t: DeskFields): string {
  const where = DESK_LABELS[t.desk_location];
  if (t.desk_location === 'done') return where;
  if (t.desk_location === 'cases') {
    const wait = courtWaitsOn(t.case_court, t.routed_to);
    return wait ? `${where} · ${wait}` : where;
  }
  if (!t.routed_to || !t.routed_to.trim()) return `${where} · needs a home`;
  return `${where} · waiting on ${firstName(t.routed_to)}`;
}

type HrefFields = Pick<TrackedThread, 'thread_no' | 'investigation_id' | 'case_no'> & {
  desk_location: DeskLocation;
};

/** The door URL for the item: its case when in one, else the thread. */
export function deskHref(t: HrefFields): string {
  if (t.desk_location === 'cases') {
    const ref = t.case_no || t.investigation_id;
    if (ref) return `${DOOR}/cases/${encodeURIComponent(ref)}`;
  }
  return `${DOOR}/threads/${encodeURIComponent(t.thread_no)}`;
}

/** "Oct 6" in the viewer's zone; '' for a missing or bad date. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export type AskTone = 'ok' | 'bad' | 'new' | 'neutral';

export interface AskPill {
  text: string;
  tone: AskTone;
}

/** One ask's state: answered (unread?), delivered, not delivered, or not confirmed. */
export function askPill(a: ThreadAsk): AskPill {
  if (a.answered_at) {
    const when = shortDate(a.answered_at);
    const base = when ? `answered ${when}` : 'answered';
    return a.answer_unseen ? { text: `${base} · unread`, tone: 'new' } : { text: base, tone: 'ok' };
  }
  if (a.delivered === false) return { text: '✗ not delivered', tone: 'bad' };
  const when = shortDate(a.sent_at);
  if (a.delivered === true) return { text: when ? `✓ sent ${when}` : '✓ sent', tone: 'ok' };
  return { text: when ? `sent ${when} · unconfirmed` : 'not sent yet', tone: 'neutral' };
}

/** True when asks went out and none has an answer yet. */
export function noAnswers(asks: ThreadAsk[]): boolean {
  return asks.length > 0 && asks.every((a) => !a.answered_at);
}

export interface AsksForPerson {
  name: string;
  asks: ThreadAsk[];
}

/** The row's asks grouped per person, in first-asked order, so a person
 *  asked twice reads "Scott Alter [✓ sent Oct 8] [answered Oct 8 · unread]"
 *  instead of the name twice (staging E2E 2026-10-09, QT-10053). Names match
 *  case- and space-insensitively. */
export function asksByPerson(asks: ThreadAsk[]): AsksForPerson[] {
  const groups = new Map<string, AsksForPerson>();
  for (const a of asks) {
    const key = a.target_name.trim().replace(/\s+/g, ' ').toLowerCase();
    const group = groups.get(key);
    if (group) group.asks.push(a);
    else groups.set(key, { name: a.target_name.trim(), asks: [a] });
  }
  return [...groups.values()];
}
